import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { createIsUserUnavailable } from "@/hooks/useUserAvailability";
import { InspectionStatus } from "@/lib/inspectionFilters";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { cacheData, getCachedData } from "@/lib/offlineStore";
import {
  isUserQueryEnabled,
  normalizeTeamScopedQueryOptions,
  type TeamScopedQueryOptions,
} from "@/hooks/teamQueryOptions";

export interface ProfileInfo {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  is_disabled?: boolean | null;
  azure_disabled_at?: string | null;
}

export interface InspectionWithDetails {
  id: string;
  status: InspectionStatus;
  due_date: string | null;
  completed_at: string | null;
  assigned_to: string | null;
  created_by: string | null;
  position_id: string | null;
  notes: string | null;
  team_id: string | null;
  team_name: string | null;
  defect_count?: number;
  checklists: {
    name: string;
    version: string | null;
  };
  inspection_positions?: {
    name: string;
  } | null;
  assigned_profile?: ProfileInfo | null;
  created_profile?: ProfileInfo | null;
  isAssigneeUnavailable?: boolean;
}

const INSPECTIONS_CACHE_KEY = "inspections-list";

// Query key factory for consistent cache management
export const inspectionsKeys = {
  all: ["inspections"] as const,
  list: (params: { userId?: string; canSeeAll: boolean; teamId?: string | null }) =>
    [...inspectionsKeys.all, "list", params] as const,
  filterOptions: ["inspections", "filterOptions"] as const,
};

export async function fetchInspections(
  userId: string | undefined,
  canSeeAll: boolean,
  isOnline: boolean = true,
  offlineEnabled: boolean = false,
  teamId?: string | null,
): Promise<InspectionWithDetails[]> {
  if (!userId) return [];

  // ── Offline path ──
  if (!isOnline && offlineEnabled) {
    const cached = await getCachedData<InspectionWithDetails[]>(INSPECTIONS_CACHE_KEY);
    return cached?.data ?? [];
  }

  let query = supabase
    .from("inspections")
    .select(
      `
      id,
      status,
      due_date,
      completed_at,
      assigned_to,
      created_by,
      position_id,
      notes,
      team_id,
      checklists (id, name, version),
      inspection_positions (id, name, is_archived)
    `,
    )
    .order("created_at", { ascending: false });

  // Filter by user if not admin/QM
  if (!canSeeAll) {
    query = query.eq("assigned_to", userId);
  }

  // Server-side team filter
  if (teamId) {
    query = query.eq("team_id", teamId);
  }

  const { data, error } = await query;

  if (error) throw error;

  // Fetch profiles for assigned_to and created_by
  const inspectionsData = data || [];
  const inspectionIds = inspectionsData.map((i) => i.id);
  const userIds = new Set<string>();
  inspectionsData.forEach((i) => {
    if (i.assigned_to) userIds.add(i.assigned_to);
    if (i.created_by) userIds.add(i.created_by);
  });

  // Collect team IDs for team name resolution
  const teamIds = [...new Set(inspectionsData.map((i) => i.team_id).filter(Boolean))] as string[];

  // Fetch profiles, defect counts, and team names in parallel
  const [profilesResult, defectCountsResult, teamsResult] = await Promise.all([
    userIds.size > 0
      ? supabase
          .from("profiles")
          .select(
            "user_id, first_name, last_name, display_name, avatar_url, is_disabled, azure_disabled_at",
          )
          .in("user_id", Array.from(userIds))
      : Promise.resolve({ data: [] }),
    inspectionIds.length > 0
      ? supabase
          .from("defects")
          .select("inspection_id")
          .in("inspection_id", inspectionIds)
          .neq("status", "cancelled")
          .is("deleted_at", null)
      : Promise.resolve({ data: [] }),
    teamIds.length > 0
      ? supabase.from("teams").select("id, name").in("id", teamIds)
      : Promise.resolve({ data: [] }),
  ]);

  // Build team name map
  const teamMap: Record<string, string> = {};
  if (teamsResult.data) {
    teamsResult.data.forEach((t) => {
      teamMap[t.id] = t.name;
    });
  }

  const profilesMap: Record<string, ProfileInfo> = {};
  if (profilesResult.data) {
    profilesResult.data.forEach((p) => {
      profilesMap[p.user_id] = p;
    });
  }

  // Count defects per inspection
  const defectCountMap: Record<string, number> = {};
  if (defectCountsResult.data) {
    defectCountsResult.data.forEach((d) => {
      if (d.inspection_id) {
        defectCountMap[d.inspection_id] = (defectCountMap[d.inspection_id] || 0) + 1;
      }
    });
  }

  // Helper to check if user is unavailable (deleted or disabled)
  const isUserUnavailable = createIsUserUnavailable(profilesMap);

  const inspectionsWithProfiles: InspectionWithDetails[] = inspectionsData.map((i) => {
    const assignedProfile = i.assigned_to ? profilesMap[i.assigned_to] : null;
    return {
      ...i,
      team_name: i.team_id ? teamMap[i.team_id] || null : null,
      assigned_profile: assignedProfile || null,
      created_profile: i.created_by ? profilesMap[i.created_by] || null : null,
      defect_count: defectCountMap[i.id] || 0,
      isAssigneeUnavailable: isUserUnavailable(i.assigned_to),
    };
  });

  // Cache for offline access
  if (offlineEnabled) {
    cacheData(INSPECTIONS_CACHE_KEY, inspectionsWithProfiles).catch(() => {});
  }

  return inspectionsWithProfiles;
}

interface FilterOption {
  id: string;
  name: string;
}

interface InspectionFilterOptions {
  checklists: FilterOption[];
  positions: FilterOption[];
  assignees: FilterOption[];
}

async function fetchFilterOptions(): Promise<InspectionFilterOptions> {
  const [checklistsRes, positionsRes, profilesRes] = await Promise.all([
    supabase.from("checklists").select("id, name").eq("is_active", true),
    supabase.from("inspection_positions").select("id, name"),
    supabase.from("profiles").select("user_id, first_name, last_name"),
  ]);

  return {
    checklists: checklistsRes.data || [],
    positions: positionsRes.data || [],
    assignees: (profilesRes.data || []).map((p) => ({
      id: p.user_id,
      name: `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Unknown",
    })),
  };
}

export function useInspectionsQuery(options?: TeamScopedQueryOptions) {
  const { user, isAdmin, isQualitaetsmanager } = useAuth();
  const canSeeAll = isAdmin || isQualitaetsmanager;
  const queryClient = useQueryClient();
  const { isOnline } = useOnlineStatus();
  const { enabled: offlineEnabled } = useFeatureFlag(
    "offline-mode",
    FEATURE_FLAGS["offline-mode"] ?? false,
  );
  const { teamId, enabled } = normalizeTeamScopedQueryOptions(options);

  const query = useQuery({
    queryKey: inspectionsKeys.list({ userId: user?.id, canSeeAll, teamId }),
    queryFn: () => fetchInspections(user?.id, canSeeAll, isOnline, offlineEnabled, teamId),
    enabled: isUserQueryEnabled(user?.id, enabled),
    placeholderData: (previousData) => previousData,
    ...(isOnline ? {} : { staleTime: Infinity, gcTime: Infinity, retry: false }),
  });

  // Use centralized realtime subscription with debouncing
  // Subscribe to inspections and defects (defect changes affect defect_count)
  useRealtimeSubscription(
    ["inspections", "defects"],
    () => queryClient.invalidateQueries({ queryKey: inspectionsKeys.all }),
    { enabled: !!user },
  );

  return {
    inspections: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useInspectionFilterOptions() {
  return useQuery({
    queryKey: inspectionsKeys.filterOptions,
    queryFn: fetchFilterOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes - filter options rarely change
  });
}
